import {ApiMethods, ApiServiceController, IApiExecutor} from "../../../../../@bizkeytech";
import ApiEndpoints from "../../endpoints";
import * as models from "./index.types";

/**@inheritDoc*/
export class OrganizationSettingsController extends ApiServiceController {

    /**@inheritDoc*/
    public constructor(executor: IApiExecutor | undefined) {
        super(executor);
    }

    /**
     * Fetches all organization settings from the server.
     *
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async list(abortController?: AbortController): models.GetOrganizationSettingsResponse {
        return this.executor?.execute({
            method: ApiMethods.get,
            url: ApiEndpoints.organizationSettings.get,
            signal: abortController?.signal,
        });
    }

    /**
     * Creates a new organization settings.
     *
     * @param request               The request to create a new organization setting.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async create(request: models.CreateOrganizationSettingsRequest, abortController?: AbortController): models.CreateOrganizationSettingsResponse {
        return this.executor?.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.organizationSettings.create,
            signal: abortController?.signal,
            data: request,
        });
    }

    /**
     * Updates an organization setting.
     *
     * @param request               The request to update an organization setting.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async update(request: models.UpdateOrganizationSettingsRequest, abortController?: AbortController): models.UpdateOrganizationSettingsResponse {
        return this.executor?.execute({
            method: ApiMethods.put,
            url: ApiEndpoints.organizationSettings.update,
            signal: abortController?.signal,
            data: request,
        });
    }

    /**
     * Deletes an organization setting.
     *
     * @param request               The request to delete an organization setting.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async delete(request: models.DeleteOrganizationSettingsRequest, abortController?: AbortController): models.DeleteOrganizationSettingsResponse {
        return this.executor?.execute({
            method: ApiMethods.delete,
            url: ApiEndpoints.organizationSettings.delete,
            signal: abortController?.signal,
            data: request,
        });
    }

    // ************************************************* Holidays *************************************************

    /**
     * Creates a new organization setting holiday.
     *
     * @param request               The request to create a new organization setting holiday.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async createHoliday(request: models.CreateOrganizationSettingHolidayRequest, abortController?: AbortController): models.CreateOrganizationSettingHolidayResponse {
        return this.executor?.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.organizationSettings.createHoliday,
            signal: abortController?.signal,
            data: request,
        });
    }

    /**
     * Updates an organization setting holiday.
     *
     * @param request               The request to update an organization setting holiday.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async updateHoliday(request: models.UpdateOrganizationSettingHolidayRequest, abortController?: AbortController): models.UpdateOrganizationSettingHolidayResponse {
        return this.executor?.execute({
            method: ApiMethods.put,
            url: ApiEndpoints.organizationSettings.updateHoliday,
            signal: abortController?.signal,
            data: request,
        });
    }

    /**
     * Deletes an organization setting holiday.
     *
     * @param request               The request to delete an organization setting holiday.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async deleteHoliday(request: models.DeleteOrganizationSettingHolidayRequest, abortController?: AbortController): models.DeleteOrganizationSettingHolidayResponse {
        return this.executor?.execute({
            method: ApiMethods.delete,
            url: ApiEndpoints.organizationSettings.deleteHoliday,
            signal: abortController?.signal,
            data: request,
        });
    }

    // ************************************************* Locations *************************************************

    /**
     * Creates a new organization setting location.
     *
     * @param request               The request to create a new organization setting location.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async createLocation(request: models.CreateOrganizationSettingLocationRequest, abortController?: AbortController): models.CreateOrganizationSettingLocationResponse {
        return this.executor?.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.organizationSettings.createLocation,
            signal: abortController?.signal,
            data: request,
        });
    }

    /**
     * Updates an organization setting location.
     *
     * @param request               The request to update an organization setting location.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async updateLocation(request: models.UpdateOrganizationSettingLocationRequest, abortController?: AbortController): models.UpdateOrganizationSettingLocationResponse {
        return this.executor?.execute({
            method: ApiMethods.put,
            url: ApiEndpoints.organizationSettings.updateLocation,
            signal: abortController?.signal,
            data: request,
        });
    }

    /**
     * Deletes an organization setting location.
     *
     * @param request               The request to delete an organization setting location.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async deleteLocation(request: models.DeleteOrganizationSettingLocationRequest, abortController?: AbortController): models.DeleteOrganizationSettingLocationResponse {
        return this.executor?.execute({
            method: ApiMethods.delete,
            url: ApiEndpoints.organizationSettings.deleteLocation,
            signal: abortController?.signal,
            data: request,
        });
    }

    // ************************************************* Qr Code *************************************************

    /**
     * Regenerates the QR code for the organization.
     *
     * @param request               The request to regenerate the QR code.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async regenerateQrCode(request: models.RegenerateOrganizationSettingQrCodeRequest, abortController?: AbortController): models.RegenerateOrganizationSettingQrCodeResponse {
        return this.executor?.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.organizationSettings.baseUrl + `/${request.organizationSettingsId}/RegenerateQrCode`,
            signal: abortController?.signal,
        });
    }

    // ************************************************* Special Work Sessions *************************************************

    /**
     * Creates a new organization setting special work session.
     *
     * @param request               The request to create a new organization setting special work session.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async createSpecialWorkSession(request: models.CreateOrganizationSettingSpecialWorkSessionRequest, abortController?: AbortController): models.CreateOrganizationSettingSpecialWorkSessionResponse {
        return this.executor?.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.organizationSettings.createSpecialWorkSession,
            signal: abortController?.signal,
            data: request,
        });
    }

    /**
     * Updates an organization setting special work session.
     *
     * @param request               The request to update an organization setting special work session.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async updateSpecialWorkSession(request: models.UpdateOrganizationSettingSpecialWorkSessionRequest, abortController?: AbortController): models.UpdateOrganizationSettingSpecialWorkSessionResponse {
        return this.executor?.execute({
            method: ApiMethods.put,
            url: ApiEndpoints.organizationSettings.updateSpecialWorkSession,
            signal: abortController?.signal,
            data: request,
        });
    }

    /**
     * Deletes an organization setting special work session.
     *
     * @param request               The request to delete an organization setting special work session.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async deleteSpecialWorkSession(request: models.DeleteOrganizationSettingSpecialWorkSessionRequest, abortController?: AbortController): models.DeleteOrganizationSettingSpecialWorkSessionResponse {
        return this.executor?.execute({
            method: ApiMethods.delete,
            url: ApiEndpoints.organizationSettings.deleteSpecialWorkSession.concat(`/${request.id}`),
            signal: abortController?.signal,
        });
    }

    // ************************************************* Work Periods *************************************************

    /**
     * Creates a new organization setting work period.
     *
     * @param request               The request to create a new organization setting work period.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async createWorkPeriod(request: models.CreateOrganizationSettingWorkPeriodRequest, abortController?: AbortController): models.CreateOrganizationSettingWorkPeriodResponse {
        return this.executor?.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.organizationSettings.createWorkPeriod,
            signal: abortController?.signal,
            data: request,
        });
    }

    /**
     * Updates an organization setting work period.
     *
     * @param request               The request to update an organization setting work period.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async updateWorkPeriod(request: models.UpdateOrganizationSettingWorkPeriodRequest, abortController?: AbortController): models.UpdateOrganizationSettingWorkPeriodResponse {
        return this.executor?.execute({
            method: ApiMethods.put,
            url: ApiEndpoints.organizationSettings.updateWorkPeriod,
            signal: abortController?.signal,
            data: request,
        });
    }

    /**
     * Deletes an organization setting work period.
     *
     * @param request               The request to delete an organization setting work period.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async deleteWorkPeriod(request: models.DeleteOrganizationSettingWorkPeriodRequest, abortController?: AbortController): models.DeleteOrganizationSettingWorkPeriodResponse {
        return this.executor?.execute({
            method: ApiMethods.delete,
            url: ApiEndpoints.organizationSettings.deleteWorkPeriod,
            signal: abortController?.signal,
            data: request,
        });
    }

    // ************************************************* Working Schedule *************************************************

    /**
     * Creates a new organization setting working schedule.
     *
     * @param request               The request to create a new organization setting working schedule.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async createWorkingSchedule(request: models.CreateOrganizationSettingWorkingScheduleRequest, abortController?: AbortController): models.CreateOrganizationSettingWorkingScheduleResponse {
        return this.executor?.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.organizationSettings.createWorkingSchedule,
            signal: abortController?.signal,
            data: request,
        });
    }

    /**
     * Updates an organization setting working schedule.
     *
     * @param request               The request to update an organization setting working schedule.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async updateWorkingSchedule(request: models.UpdateOrganizationSettingWorkingScheduleRequest, abortController?: AbortController): models.UpdateOrganizationSettingWorkingScheduleResponse {
        return this.executor?.execute({
            method: ApiMethods.put,
            url: ApiEndpoints.organizationSettings.updateWorkingSchedule,
            signal: abortController?.signal,
            data: request,
        });
    }

    /**
     * Deletes an organization setting working schedule.
     *
     * @param request               The request to delete an organization setting working schedule.
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    public async deleteWorkingSchedule(request: models.DeleteOrganizationSettingWorkingScheduleRequest, abortController?: AbortController): models.DeleteOrganizationSettingWorkingScheduleResponse {
        return this.executor?.execute({
            method: ApiMethods.delete,
            url: ApiEndpoints.organizationSettings.deleteWorkingSchedule,
            signal: abortController?.signal,
            data: request,
        });
    }


}


export * from './index.types';
export default OrganizationSettingsController;